import styled, { css, keyframes } from "styled-components";

interface SkinProps {
  $isGriffle?: boolean;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const BellButton = styled.button<SkinProps>`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ $isGriffle }) => ($isGriffle ? "rgba(99, 102, 241, 0.1)" : "rgba(0, 0, 0, 0.05)")};
  }

  svg {
    width: 22px;
    height: 22px;
    color: ${({ $isGriffle }) => ($isGriffle ? "#6366f1" : "#495057")};
  }
`;

export const UnreadBadge = styled.span<SkinProps>`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background-color: ${({ $isGriffle }) => ($isGriffle ? "#6366f1" : "#dc3545")};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  pointer-events: none;
`;

export const Dropdown = styled.div<SkinProps>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-height: 440px;
  background: #fff;
  border-radius: ${({ $isGriffle }) => ($isGriffle ? "12px" : "8px")};
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease-out;
  overflow: hidden;
  font-family: ${({ $isGriffle }) =>
    $isGriffle ? "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" : "inherit"};
`;

export const DropdownHeader = styled.div<SkinProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: ${({ $isGriffle }) => ($isGriffle ? "#1e1b4b" : "#212529")};
  }
`;

export const MarkAllReadButton = styled.button<SkinProps>`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $isGriffle }) => ($isGriffle ? "#6366f1" : "#0d6efd")};
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ $isGriffle }) => ($isGriffle ? "rgba(99, 102, 241, 0.08)" : "rgba(13, 110, 253, 0.08)")};
  }
`;

export const NotificationList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

export const NotificationItem = styled.div<SkinProps & { $isRead: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f8f8f8;

  ${({ $isRead }) =>
    !$isRead &&
    css`
      background-color: #f0f7ff;
    `}

  &:hover {
    background-color: ${({ $isRead }) => ($isRead ? "#f8f9fa" : "#e8f0fe")};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const NotificationIcon = styled.div<SkinProps & { $type: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;

  ${({ $type }) => {
    switch ($type) {
      case "assignment_due":
        return css`
          background-color: #fff3cd;
          color: #856404;
        `;
      case "student_inactive":
        return css`
          background-color: #f8d7da;
          color: #842029;
        `;
      case "quiz_results":
        return css`
          background-color: #d1e7dd;
          color: #0f5132;
        `;
      case "milestone":
        return css`
          background-color: #cfe2ff;
          color: #084298;
        `;
      default:
        return css`
          background-color: #e9ecef;
          color: #495057;
        `;
    }
  }}
`;

export const NotificationBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const NotificationTitle = styled.p<{ $isRead: boolean }>`
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: ${({ $isRead }) => ($isRead ? 400 : 600)};
  color: #212529;
  line-height: 1.4;
`;

export const NotificationDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NotificationTimestamp = styled.span`
  font-size: 11px;
  color: #adb5bd;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 2px;
`;

export const DropdownFooter = styled.div<SkinProps>`
  padding: 10px 16px;
  border-top: 1px solid #f0f0f0;
  text-align: center;

  a {
    font-size: 13px;
    font-weight: 500;
    color: ${({ $isGriffle }) => ($isGriffle ? "#6366f1" : "#0d6efd")};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;
